<template>
  <v-app>
    <v-container>
      <v-row>
        <v-col cols="12">
          <v-card class="mb-4">
            <v-card-title class="headline">
              My Bookings
              <v-spacer></v-spacer>
              <v-btn color="grey" @click="goBack" dark>
                <v-icon left class="material-icons-round">arrow_left</v-icon>
                Back
              </v-btn>
            </v-card-title>

            <!-- Current & Upcoming Bookings -->
            <v-card-subtitle class="pt-4 pb-2">
              <h2 class="text-h5 primary--text">Current & Upcoming Bookings</h2>
            </v-card-subtitle>

            <v-card-text>
              <v-data-table
                :headers="headers"
                :items="currentBookings"
                :loading="loading"
                loading-text="Loading bookings..."
                no-data-text="No current or upcoming bookings found"
                class="elevation-1 mb-8"
              >
                <template v-slot:item.vehicle="{ item }">
                  {{ item.vehicle[0].year }} {{ item.vehicle[0].make }} {{ item.vehicle[0].model }}
                </template>

                <template v-slot:item.booking_period="{ item }">
                  {{ formatDate(item.start_datetime) }} -
                  {{ formatDate(item.end_datetime) }}
                </template>

                <template v-slot:item.total_amount="{ item }">
                  ${{ item.total_amount }}
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    text-color="white"
                    small
                  >
                    {{ item.status }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn
                    small
                    color="blue"
                    text
                    @click="viewBookingDetails(item)"
                  >
                  <v-icon small class="material-icons-round mr-2">visibility</v-icon>
                    View
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>

            <!-- Past Bookings -->
            <v-card-subtitle class="pt-4 pb-2">
              <h2 class="text-h5 grey--text text--darken-1">Past Bookings</h2>
            </v-card-subtitle>

            <v-card-text>
              <v-data-table
                :headers="headers"
                :items="pastBookings"
                :loading="loading"
                loading-text="Loading bookings..."
                no-data-text="No past bookings found"
                class="elevation-1"
              >
                <template v-slot:item.vehicle="{ item }">
                  {{ item.vehicle[0].year }} {{ item.vehicle[0].make }} {{ item.vehicle[0].model }}
                </template>

                <template v-slot:item.booking_period="{ item }">
                  {{ formatDateTime(item.start_datetime) }}  -
                  {{ formatDateTime(item.end_datetime) }} 
                </template>

                <template v-slot:item.total_amount="{ item }">
                  ${{ item.total_amount }}
                </template>

                <template v-slot:item.status="{ item }">
                  <v-chip
                    :color="getStatusColor(item.status)"
                    text-color="white"
                    small
                  >
                    {{ item.status }}
                  </v-chip>
                </template>

                <template v-slot:item.actions="{ item }">
                  <v-btn
                    small
                    color="primary"
                    text
                    @click="viewBookingDetails(item)"
                  >
                    <v-icon small class="material-icons-round mr-2">visibility</v-icon>
                    View
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Booking Details Dialog -->
      <v-dialog v-model="detailsDialog" max-width="600px">
        <v-card v-if="selectedBooking">
          <v-card-title class="headline">
            Booking Details
            <v-spacer></v-spacer>
            <v-btn icon @click="detailsDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text>
            <v-row>
              <v-col cols="12">
                <h3 class="subtitle-1 font-weight-bold">Vehicle</h3>
                <p>{{ selectedBooking.vehicle[0].year }} {{ selectedBooking.vehicle[0].make }} {{ selectedBooking.vehicle[0].model }}</p>
              </v-col>

              <v-col cols="12" sm="6">
                <h3 class="subtitle-1 font-weight-bold">Start Date & Time</h3>
                <p>{{ formatDateTime(selectedBooking.start_datetime) }}</p>
              </v-col>

              <v-col cols="12" sm="6">
                <h3 class="subtitle-1 font-weight-bold">End Date & Time</h3>
                <p>{{ formatDateTime(selectedBooking.end_datetime) }}</p>
              </v-col>

              <v-col cols="12" sm="6">
                <h3 class="subtitle-1 font-weight-bold">Total Amount</h3>
                <p>${{ selectedBooking.total_amount }}</p>
              </v-col>

              <v-col cols="12" sm="6">
                <h3 class="subtitle-1 font-weight-bold">Status</h3>
                <v-chip
                  :color="getStatusColor(selectedBooking.status)"
                  text-color="white"
                  small
                >
                  {{ selectedBooking.status }}
                </v-chip>
              </v-col>

              <v-col cols="12" v-if="selectedBooking.discount_amount > 0">
                <h3 class="subtitle-1 font-weight-bold">Discount Applied</h3>
                <p>${{ selectedBooking.discount_amount }}</p>
              </v-col>

              <v-col cols="12">
                <h3 class="subtitle-1 font-weight-bold">Booking Date</h3>
                <p>{{ formatDateTime(selectedBooking.created_at) }}</p>
              </v-col>
            </v-row>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="detailsDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-app>
</template>

<script>
export default {
  name: 'RentalBookings',

  data() {
    return {
      loading: true,
      currentBookings: [],
      pastBookings: [],
      headers: [
        { text: 'Vehicle', value: 'vehicle', sortable: true },
        { text: 'Booking Period', value: 'booking_period', sortable: true },
        { text: 'Total Amount', value: 'total_amount', sortable: true },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Actions', value: 'actions', sortable: false }
      ],
      detailsDialog: false,
      selectedBooking: null
    };
  },

  created() {
    this.checkAuth();
    this.fetchBookings();
  },

  methods: {
    checkAuth() {
      // Check if user is logged in
      const token = localStorage.getItem('rental_token');
      if (!token) {
        // Redirect to login page if not logged in
        this.$router.push('/rental/login');
      }
    },

    fetchBookings() {
      this.loading = true;

      // Get token from localStorage
      const token = localStorage.getItem('rental_token');

      // Fetch bookings from API
      window.axios.get('/api/auth/rental/bookings', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (response.data.flag === 1) {
          // Process current and upcoming bookings
          this.currentBookings = this.processBookings(response.data.future_bookings || []);

          // Process past bookings
          this.pastBookings = this.processBookings(response.data.past_bookings || []);

          // If the API doesn't return past_bookings, we'll need to filter them manually
          if (!response.data.past_bookings && response.data.bookings) {
            this.separateBookings(response.data.bookings);
          }
        } else {
          this.$toast.error('Failed to load bookings', {
            position: 'top-right'
          });
        }
      })
      .catch(error => {
        console.error('Error fetching bookings:', error);
        this.$toast.error('An error occurred while loading bookings', {
          position: 'top-right'
        });
      })
      .finally(() => {
        this.loading = false;
      });
    },

    processBookings(bookings) {
      return bookings.map(booking => {
        return {
          ...booking,
          vehicle_year: booking.vehicle ? booking.vehicle.year : 'N/A',
          vehicle_make: booking.vehicle ? booking.vehicle.make : 'N/A',
          vehicle_model: booking.vehicle ? booking.vehicle.model : 'N/A'
        };
      });
    },

    separateBookings(bookings) {
      const now = new Date();
      const processed = this.processBookings(bookings);

      // Separate bookings into past and current/future
      processed.forEach(booking => {
        const endDate = new Date(`${booking.end_date} ${booking.end_time}`);

        if (endDate < now) {
          this.pastBookings.push(booking);
        } else {
          this.currentBookings.push(booking);
        }
      });

      // Sort bookings by date (newest first for past, soonest first for current)
      this.pastBookings.sort((a, b) => new Date(`${b.start_date} ${b.start_time}`) - new Date(`${a.start_date} ${a.start_time}`));
      this.currentBookings.sort((a, b) => new Date(`${a.start_date} ${a.start_time}`) - new Date(`${b.start_date} ${b.start_time}`));
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';
      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getStatusColor(status) {
      switch (status.toLowerCase()) {
        case 'confirmed':
          return 'green';
        case 'pending':
          return 'warning';
        case 'cancelled':
          return 'red';
        default:
          return 'grey';
      }
    },

    viewBookingDetails(booking) {
      this.selectedBooking = booking;
      this.detailsDialog = true;
    },

    goBack() {
      this.$router.go(-1);
    }
  }
};
</script>

<style scoped>
.headline {
  display: flex;
  align-items: center;
}

.v-card-subtitle {
  border-top: 1px solid #e0e0e0;
}

.v-card-subtitle h2 {
  margin-bottom: 0;
  font-weight: 500;
}

.v-data-table {
  border-radius: 8px;
}
</style>
